import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import type { RootState } from '../index'

interface AudioRecorderState {
  isInitializingSession: boolean
  isRecording: boolean
  isSummarizing: boolean
  recordingCorrelationId: string | null
  recordingDischargeNotes: string | null
  recordingMedicalNotes: string | null
  recordingPatientId: string | null
  recordingSoapId: string | null
}

const initialState: AudioRecorderState = {
  isRecording: false,
  isSummarizing: false,
  recordingCorrelationId: null,
  recordingPatientId: null,
  recordingSoapId: null,
  recordingMedicalNotes: null,
  recordingDischargeNotes: null,
  isInitializingSession: false,
}

const audioRecorderSlice = createSlice({
  name: 'audioRecorder',
  initialState,
  reducers: {
    setRecording: (state, action: PayloadAction<boolean>) => {
      state.isRecording = action.payload
    },
    setSummarizing: (state, action: PayloadAction<boolean>) => {
      state.isSummarizing = action.payload
    },
    setRecordingCorrelationId: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.recordingCorrelationId = action.payload
    },
    setRecordingPatientId: (state, action: PayloadAction<string | null>) => {
      state.recordingPatientId = action.payload
    },
    setRecordingSoapId: (state, action: PayloadAction<string | null>) => {
      state.recordingSoapId = action.payload
    },
    setRecordingMedicalNotes: (state, action: PayloadAction<string | null>) => {
      state.recordingMedicalNotes = action.payload
    },
    setRecordingDischargeNotes: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.recordingDischargeNotes = action.payload
    },
    setInitializingSession: (state, action: PayloadAction<boolean>) => {
      state.isInitializingSession = action.payload
    },
    clearRecordingContext: (state) => {
      state.recordingCorrelationId = null
      state.recordingPatientId = null
      state.recordingSoapId = null
      state.recordingMedicalNotes = null
      state.recordingDischargeNotes = null
    },
  },
})

export const {
  setRecording,
  setSummarizing,
  setRecordingCorrelationId,
  setRecordingPatientId,
  setRecordingSoapId,
  setRecordingMedicalNotes,
  setRecordingDischargeNotes,
  setInitializingSession,
  clearRecordingContext,
} = audioRecorderSlice.actions

export default audioRecorderSlice.reducer

// Selectors
export const getAudioRecorderState = (state: RootState): AudioRecorderState =>
  state.audioRecorder
export const getIsRecording = (state: RootState) =>
  getAudioRecorderState(state).isRecording
export const getIsSummarizing = (state: RootState) =>
  getAudioRecorderState(state).isSummarizing
export const getRecordingCorrelationId = (state: RootState) =>
  getAudioRecorderState(state).recordingCorrelationId
export const getRecordingPatientId = (state: RootState) =>
  getAudioRecorderState(state).recordingPatientId
export const getRecordingSoapId = (state: RootState) =>
  getAudioRecorderState(state).recordingSoapId
export const getRecordingMedicalNotes = (state: RootState): string | null =>
  getAudioRecorderState(state).recordingMedicalNotes
export const getRecordingDischargeNotes = (state: RootState): string | null =>
  getAudioRecorderState(state).recordingDischargeNotes
export const getIsInitializingSession = (state: RootState) =>
  getAudioRecorderState(state).isInitializingSession

/**
 * Convenience selector to check if any audio recorder operation is in progress.
 * UI components can use this to show loading states when any operation is active.
 */
export const getIsAudioRecorderBusy = (state: RootState) => {
  const recorderState = getAudioRecorderState(state)
  return (
    recorderState.isInitializingSession ||
    recorderState.isRecording ||
    recorderState.isSummarizing
  )
}
